import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useExperience, usePersonalise } from '@/api/hooks';
import type { WireExperiencePayload } from '@/api/wire';
import { PREFERENCE, asTheme, currentTheme, setTheme, subscribeTheme, type Theme } from './theme';

const EMPTY: WireExperiencePayload = { views: {}, workflows: {}, capabilities: {}, preferences: {} };

/**
 * The theme as every control sees it, and the one way to change it: applied
 * at once, then saved to the person's own line of the experience plane so
 * the next browser opens the same way (§9). A save that is refused leaves
 * the theme applied here — it is a preference, not a permission.
 */
export function useTheme(): { theme: Theme; choose: (next: Theme) => void } {
  const theme = useSyncExternalStore(subscribeTheme, currentTheme, () => 'light' as const);
  const experience = useExperience();
  const personalise = usePersonalise();

  const choose = useCallback(
    (next: Theme) => {
      setTheme(next);
      const mine = experience.data?.line.at(-1)?.payload ?? EMPTY;
      if (mine.preferences[PREFERENCE] === next) return;
      personalise.mutate({ ...mine, preferences: { ...mine.preferences, [PREFERENCE]: next } });
    },
    [experience.data, personalise],
  );

  return { theme, choose };
}

/**
 * Mounted once, inside the signed-in frame (so it never asks before there
 * is a session): the saved preference wins over this browser's memory.
 * Only `appearance`, which only the Appearance control writes, and only
 * an explicit `light` or `dark`; the retired `theme` preference says
 * nothing, and light stands (see theme.ts).
 */
export function SavedTheme(): null {
  useSavedTheme();
  return null;
}

function useSavedTheme(): void {
  const experience = useExperience();
  const saved = experience.data?.resolved.preferences[PREFERENCE];
  useEffect(() => {
    if (saved !== 'light' && saved !== 'dark') return;
    if (asTheme(saved) !== currentTheme()) setTheme(saved);
  }, [saved]);
}
